import "./FileAttachmentsUploaderStyles.scss";

import * as React from "react";
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import {
    IAttachmentsUploadConfigVm
} from "../../interfaces";
import {
    AttachmentRelatesTo,
    DefaultFileAttachmentsUploaderSettings,
    FileAttachmentsTableMode
} from "../../models";
import FileAttachmentsTable from "../viewer/FileAttachmentsTable";
import { Authorization } from "../../../misc/components/Authorization";
import { DragDrop } from "../../../misc/DragDrop";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { File } from "webportalclient";
import { Bytes } from "@vitalograph/web-ui";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";

export interface FileAttachmentsUploaderProps
    extends IAttachmentsUploadConfigVm {
    title?: string;
    requiresPermissions?: string[];
    onFileAttachmentsUpdated?: (fileAttachments: any[]) => void;
    formState;
}

const FileAttachmentsUploader = (props: FileAttachmentsUploaderProps) => {
    const [showExceedingFileCount, setShowExceedingFileCount] = React.useState(false);
    const [showValidationMessage, setShowValidationMessage] = React.useState(false);
    const [fileAttachments, setFileAttachments] = React.useState([]);
    const [dropZoneDisabled, setDropZoneDisabled] = React.useState(false);
    const [defaultSettings, setDefaultSettings] = React.useState(DefaultFileAttachmentsUploaderSettings);
    const legacyClient = useWebPortalLegacyClient();

    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        if (!props.formState.values.CorrectionRecords) {
            setFileAttachments([]);
            setDropZoneDisabled(false);
        }
    }, [props.formState.values.CorrectionRecords])

    const onDrop = (acceptedFiles: any[], rejectedFiles: any[]) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            setShowExceedingFileCount(false);
            setShowValidationMessage(true);
            return;
        }

        const nextFileCount = fileAttachments.length + acceptedFiles.length;
        const fileCountExceeded = nextFileCount > getSettingMaxFileCount();

        setShowExceedingFileCount(fileCountExceeded);

        if (fileCountExceeded) {
            return;
        }

        const nextFiles = fileAttachments.slice().concat(acceptedFiles);

        setFileAttachments(nextFiles);
        setDropZoneDisabled(isMaxFileCountAchieved(nextFileCount));
        setShowExceedingFileCount(fileCountExceeded);
        setShowValidationMessage(false);

        updateParentWithNextFiles(nextFiles);
    }

    const updateParentWithNextFiles = (nextFiles: any[]) => {
        if (props.onFileAttachmentsUpdated) {
            props.onFileAttachmentsUpdated(nextFiles);
        }
    }

    const getSettingTitle = () => {
        return props.title ? props.title : "File Attachments";
    }

    const getSettingMaxFileCount = () => {
        return props.maxFileCount ? props.maxFileCount : defaultSettings.maxFileCount;
    }

    const getSettingAllowedFileTypeExtensions = () => {
        return props.allowedFileTypeExtensions
            ? props.allowedFileTypeExtensions
            : defaultSettings.allowedFileTypeExtensions;
    }

    const getSettingMaxFileSize = () => {
        return props.maxFileSize ? props.maxFileSize : defaultSettings.maxFileSize;
    }

    const getAllowMultipleFiles = () => {
        return getSettingMaxFileCount() > 1;
    }
    const renderAttachFilesActionButton = () => {
        return (
            <button
                className={
                    "attach-files btn btn-xs btn-info" +
                    (isMaxFileCountAchieved() ? " disabled" : "")
                }
                type="button"
                onClick={() => document.getElementById('FileAttachments').click()}
            >
                <span className="fa fa-paperclip" aria-hidden="true" />
                &nbsp;Attach File
                {getAllowMultipleFiles() ? "s" : ""}
                {isMaxFileCountAchieved() && " (maximum files attached)"}
            </button>
        );
    }

    const tryRenderDropzone = () => {
        return (
            !dropZoneDisabled && (
                <div className="col-lg-12">
                    <DragDrop
                        id="FileAttachments"
                        options={{
                            disabled: dropZoneDisabled,
                            onDrop: onDrop,
                            accept: getSettingAllowedFileTypeExtensions(),
                            maxSize: getSettingMaxFileSize(),
                            multiple: getAllowMultipleFiles(),
                        }}
                    />
                </div>
            )
        );
    }

    const tryRenderWarningFileLimitExceeded = () => {
        return (
            showExceedingFileCount && (
                <div className="alert alert-danger">
                    Please select less multiple files as with the last try you have exceeded the
                    limit of max
                    {" " + getSettingMaxFileCount()} file attachments per submission.
                </div>
            )
        );
    }

    const tryRenderWarningAttachmentValidation = () => {
        return (
            <>
                {tryRenderWarningFileLimitExceeded()}

                {showValidationMessage && (
                    <div className="alert alert-danger">
                        Attachments were not added! You have selected files with unsupported file
                        type or exceeding file size. Please try again with valid file types{" "}
                        <strong>({getSettingAllowedFileTypeExtensions()})</strong>{" "}
                        and file size up to{" "}
                        <Bytes value={getSettingMaxFileSize()} />.
                    </div>
                )}
            </>
        );
    }

    const isMaxFileCountAchieved = (filesCount?: number) => {
        return (
            (filesCount ? filesCount : fileAttachments.length) >=
            getSettingMaxFileCount()
        );
    }

    const removeAttachment = (fileIndex: number) => {
        const nextFileCount = fileAttachments.length - 1;
        const exceedingFileCount = nextFileCount > getSettingMaxFileCount();

        let nextFiles = fileAttachments.slice();
        nextFiles.splice(fileIndex, 1);

        setDropZoneDisabled(isMaxFileCountAchieved(nextFileCount));
        setFileAttachments(nextFiles);
        setShowExceedingFileCount(exceedingFileCount);

        updateParentWithNextFiles(nextFiles);
    }

    return (
        <Authorization
            permissionIdsAny={props.requiresPermissions}
            studyId={study.id}
            currentPermissions={currentUserPermissions}
        >
            <div className="fileAttachmentsUploader col-lg-12">
                <ErrorBoundary>
                    {tryRenderWarningAttachmentValidation()}

                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title pull-left heading">
                                {getSettingTitle()}
                                {getAllowMultipleFiles()
                                    ? ` (${fileAttachments.length
                                    }/${getSettingMaxFileCount()})`
                                    : ""}
                            </h3>
                            <div
                                className="btn-group pull-right"
                                style={{ height: "12px" }}
                            >
                                {renderAttachFilesActionButton()}
                            </div>
                            <span className="clearfix" />
                        </div>
                        <div className="panel-body">
                            <FileAttachmentsTable
                                legacyClient={legacyClient}
                                fileAttachments={fileAttachments.map(fa => ({ fileName: fa.name, fileSizeInBytes: fa.size }))}
                                mode={FileAttachmentsTableMode.ModifyList}
                                onRemoveAttachment={removeAttachment}
                            />
                            {tryRenderDropzone()}
                        </div>
                    </div>
                </ErrorBoundary>
            </div>
        </Authorization>
    );
}

export default FileAttachmentsUploader;
